
import React, { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Calendar } from '@/components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { format, addDays, set, addMinutes } from 'date-fns';
import { CalendarIcon, Calendar as CalendarLucide, Loader2 } from 'lucide-react';
import { cn } from '@/lib/utils';
import { useToast } from '@/hooks/use-toast';
import { supabase } from '@/integrations/supabase/client';

interface ScheduleDemoDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

const ScheduleDemoDialog: React.FC<ScheduleDemoDialogProps> = ({ open, onOpenChange }) => {
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [notes, setNotes] = useState('');
  const [date, setDate] = useState<Date | undefined>(undefined);
  const [time, setTime] = useState<string>('10:00');
  const [isLoading, setIsLoading] = useState(false);
  const [isSuccessful, setIsSuccessful] = useState(false);
  const { toast } = useToast();

  // Available times for selection
  const availableTimes = Array.from({ length: 18 }, (_, i) => {
    const hour = 8 + Math.floor(i / 2);
    const minute = (i % 2) * 30;
    return `${hour.toString().padStart(2, '0')}:${minute.toString().padStart(2, '0')}`;
  });

  const resetForm = () => {
    setName('');
    setEmail('');
    setPhone('');
    setNotes('');
    setDate(undefined);
    setTime('10:00');
    setIsSuccessful(false);
  };

  const handleSchedule = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!name || !email || !date) {
      toast({
        variant: "destructive",
        title: "Missing information",
        description: "Please fill all required fields.",
      });
      return;
    }

    setIsLoading(true);

    try {
      // Combine date and time
      const [hours, minutes] = time.split(':').map(Number);
      const scheduledDateTime = set(date, { hours, minutes });

      // Call Supabase Edge Function
      const { data, error } = await supabase.functions.invoke('google-calendar', {
        body: {
          name,
          email, 
          phone,
          notes,
          date: scheduledDateTime.toISOString(),
          duration: 30 // 30 minute meeting
        },
        method: 'POST'
      });

      if (error) {
        throw new Error(error.message);
      }

      if (data.error) {
        throw new Error(data.error);
      }

      setIsSuccessful(true);
      toast({
        title: "Meeting scheduled",
        description: "Your demo has been scheduled successfully. Check your email for details.",
      });

    } catch (error) {
      console.error('Error scheduling demo:', error);
      toast({
        variant: "destructive",
        title: "Failed to schedule",
        description: error instanceof Error ? error.message : "An error occurred while scheduling your demo.",
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleClose = () => {
    if (!isLoading) {
      resetForm();
      onOpenChange(false);
    }
  };

  if (isSuccessful) {
    return (
      <Dialog open={open} onOpenChange={handleClose}>
        <DialogContent className="sm:max-w-[500px]">
          <DialogHeader>
            <DialogTitle className="text-center text-2xl">Demo Scheduled!</DialogTitle>
            <DialogDescription className="text-center">
              Thank you for scheduling a demo. You will receive a confirmation email shortly with all the details.
            </DialogDescription>
          </DialogHeader>
          <div className="flex justify-center my-6">
            <div className="bg-green-100 rounded-full p-6">
              <CalendarLucide className="h-12 w-12 text-green-600" />
            </div>
          </div>
          <DialogFooter>
            <Button className="w-full" onClick={handleClose}>Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    );
  }

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-[500px]">
        <DialogHeader>
          <DialogTitle>Schedule a Demo</DialogTitle>
          <DialogDescription>
            Book a personalized demo with our team to see how our referral platform works.
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={handleSchedule} className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="name">Name *</Label>
            <Input
              id="name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Your full name"
              required
            />
          </div>
          
          <div className="space-y-2">
            <Label htmlFor="email">Email Address *</Label>
            <Input
              id="email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="your-email@example.com"
              required
            />
          </div>
          
          <div className="space-y-2">
            <Label htmlFor="phone">Phone Number (optional)</Label>
            <Input
              id="phone"
              value={phone}
              onChange={(e) => setPhone(e.target.value)}
              placeholder="+1 (123) 456-7890"
            />
          </div>
          
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="date">Date *</Label>
              <Popover>
                <PopoverTrigger asChild>
                  <Button
                    variant={"outline"}
                    className={cn(
                      "w-full justify-start text-left font-normal",
                      !date && "text-muted-foreground"
                    )}
                  >
                    <CalendarIcon className="mr-2 h-4 w-4" />
                    {date ? format(date, "PPP") : <span>Pick a date</span>}
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0">
                  <Calendar
                    mode="single"
                    selected={date}
                    onSelect={setDate}
                    disabled={(date) => 
                      date < addDays(new Date(), 1) || // No dates earlier than tomorrow
                      date.getDay() === 0 || // No Sundays
                      date.getDay() === 6 // No Saturdays
                    }
                    initialFocus
                  />
                </PopoverContent>
              </Popover>
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="time">Time *</Label>
              <select
                id="time"
                className="w-full h-10 px-3 rounded-md border border-input bg-background"
                value={time}
                onChange={(e) => setTime(e.target.value)}
                required
              >
                {availableTimes.map((time) => (
                  <option key={time} value={time}>
                    {time}
                  </option>
                ))}
              </select>
            </div>
          </div>
          
          <div className="space-y-2">
            <Label htmlFor="notes">Additional Notes (optional)</Label>
            <Textarea
              id="notes"
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="Any specific topics or questions you'd like to discuss?"
              className="min-h-[80px]"
            />
          </div>
          
          <DialogFooter>
            <Button type="submit" disabled={isLoading} className="w-full">
              {isLoading ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Scheduling...
                </>
              ) : (
                <>Schedule Demo</>
              )}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default ScheduleDemoDialog;
